import React from 'react';
import { Button } from '@/components/ui/button';
import { ArrowRight, Star } from 'lucide-react';
import { trackClick } from '@/utils/analytics';
import happyFamilyImage from '@/assets/happy-family.jpg';
import { LifeInsuranceStats } from '@/components/LifeInsuranceStats';
import { useIsMobile } from '@/hooks/use-mobile';
import { MobileCarousel } from '@/components/MobileCarousel';
import modernFamilyHero from '@/assets/hero/modern-family-hero.jpg';
interface HeroSectionProps {
  onGetStarted: () => void;
}
export const HeroSection: React.FC<HeroSectionProps> = ({
  onGetStarted
}) => {
  const isMobile = useIsMobile();

  const handleGetStartedClick = () => {
    trackClick('hero_get_started_button', 'primary_cta');
    onGetStarted();
  };
  if (isMobile) {
    return <>
        <section className="relative bg-white pt-16 pb-6 px-4">
          {/* Main Content - Above the fold */}
          <div className="w-full max-w-sm mx-auto text-center space-y-3">
            {/* Mobile Hero - Clean and Professional */}
            <h1 className="text-3xl md:text-5xl font-bold leading-tight text-primary">
              Life Insurance
              <br />
              Made Simple
            </h1>
            
            <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
              AI-powered matching with <span className="font-semibold text-foreground">top carriers</span>—find the right policy for you, not just one company.
            </p>
            
            <p className="text-base text-muted-foreground leading-relaxed">
              We compare all the leading life insurance carriers and use your information to connect you with the best fit, <span className="font-semibold text-foreground">instantly</span>.
            </p>
            
            <Button onClick={handleGetStartedClick} size="lg" className="w-full py-3 text-base font-semibold bg-primary text-primary-foreground hover:bg-primary/90 shadow-md rounded-xl transform hover:scale-[1.02] transition-all duration-300 mt-4">
              Find My Best Match
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            
            {/* Trust Badge */}
            <div className="flex items-center justify-center space-x-2 text-sm text-muted-foreground mt-2">
              <div className="flex items-center space-x-1">
                {[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 text-yellow-400 fill-current" />)}
              </div>
              <span className="font-semibold">4.8/5</span>
              <span>from 1,000+ customers</span>
            </div>

            {/* Hero Image Container */}
            <div className="mt-4 h-[220px] md:h-[320px] w-full overflow-hidden rounded-2xl shadow relative">
              <div className="absolute inset-0 bg-gradient-to-t from-black/15 to-transparent z-10"></div>
              <img 
                src={modernFamilyHero} 
                alt="Happy modern family enjoying life together" 
                className="object-cover object-center w-full h-full" 
                loading="lazy"
                decoding="async"
                fetchPriority="low"
                width="1000" 
                height="700" 
              />
            </div>
          </div>
        </section>
      </>;
  }
  return <>
      <section className="relative min-h-screen flex items-center justify-center bg-gradient-to-b from-background to-primary-light/30">
        <div className="container mx-auto px-[25px] py-[20px]">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-7xl mx-auto">
            {/* Left Column - Text Content */}
            <div className="text-center lg:text-left">
              {/* Main Headline */}
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-foreground mb-6 leading-tight tracking-tight px-0 py-[30px]">
                Life Insurance
                <br />
                <span className="text-primary">Made Simple</span>
              </h1>
              
              <p className="text-lg md:text-xl font-semibold text-foreground mb-4">
                AI-powered matching with top carriers—find the right policy for you, not just one company.
              </p>
              
              {/* Subheadline */}
              <p className="text-lg md:text-xl text-muted-foreground mb-12 leading-relaxed">
                We compare all the leading life insurance carriers and use your information to connect you with the best fit, instantly.
              </p>
              
              {/* Primary CTA */}
              <div className="mb-12">
                <Button onClick={handleGetStartedClick} size="lg" variant="hero" className="h-16 px-12 text-lg font-semibold shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all duration-300">
                  Find My Best Match
                  <ArrowRight className="ml-2 h-6 w-6" />
                </Button>
              </div>
              
              {/* Trust Indicators */}
              <div className="flex flex-col md:flex-row items-center justify-center lg:justify-start space-y-4 md:space-y-0 md:space-x-8 text-sm text-muted-foreground">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-primary rounded-full"></div>
                  <span>Free, no obligations</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-primary rounded-full"></div>
                  <span>Takes 2 minutes</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-primary rounded-full"></div>
                  <span>Licensed & secure</span>
                </div>
              </div>
            </div>

            {/* Right Column - Family Image */}
            <div className="relative">
              <img src={happyFamilyImage} alt="Happy family enjoying life insurance protection" className="w-full h-auto rounded-2xl shadow-2xl object-cover" loading="eager" fetchPriority="high" width="600" height="400" />
            </div>
          </div>
        </div>
        
        {/* Bottom fade */}
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent pointer-events-none"></div>
      </section>

      {/* Coverage Adjustment Section */}
      <section className="py-16 bg-background md:py-[70px]">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12 md:mb-16">
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-4 md:mb-6">
                Adjust Your Coverage <span className="text-primary">As Life Changes</span>
              </h2>
              <p className="text-base md:text-lg text-muted-foreground max-w-3xl mx-auto leading-relaxed">
                Life isn't static. That's why Safurra's AI-powered platform makes it simple to review and adjust your coverage anytime — so protection always fits your needs.
              </p>
            </div>
            
            {/* Mobile Carousel */}
            {isMobile ? <MobileCarousel items={[<div className="bg-background rounded-2xl p-6 border border-border shadow-sm text-center">
                    <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <div className="text-2xl" aria-hidden="true">🔄</div>
                    </div>
                    <h3 className="text-xl font-semibold text-foreground mb-3">
                      Flexible
                    </h3>
                    <p className="text-muted-foreground">
                      Update your policy as life changes.
                    </p>
                  </div>, <div className="bg-background rounded-2xl p-6 border border-border shadow-sm text-center">
                    <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <div className="text-2xl" aria-hidden="true">🤖</div>
                    </div>
                    <h3 className="text-xl font-semibold text-foreground mb-3">
                      Smart Matching
                    </h3>
                    <p className="text-muted-foreground">
                      Our AI finds your best-fit carrier.
                    </p>
                  </div>, <div className="bg-background rounded-2xl p-6 border border-border shadow-sm text-center">
                    <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                      <div className="text-2xl" aria-hidden="true">🛡️</div>
                    </div>
                    <h3 className="text-xl font-semibold text-foreground mb-3">
                      Peace of Mind
                    </h3>
                    <p className="text-muted-foreground">
                      Always the right protection at the right time.
                    </p>
                  </div>]} ariaLabel="Coverage benefits carousel" /> : (/* Desktop Grid */
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
                {/* Flexible */}
                <div className="text-center">
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <div className="text-2xl" aria-hidden="true">🔄</div>
                  </div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">
                    Flexible
                  </h3>
                  <p className="text-muted-foreground">
                    Update your policy as life changes.
                  </p>
                </div>

                {/* Smart Matching */}
                <div className="text-center">
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <div className="text-2xl" aria-hidden="true">🤖</div>
                  </div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">
                    Smart Matching
                  </h3>
                  <p className="text-muted-foreground">
                    Our AI finds your best-fit carrier.
                  </p>
                </div>

                {/* Peace of Mind */}
                <div className="text-center">
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <div className="text-2xl" aria-hidden="true">🛡️</div>
                  </div>
                  <h3 className="text-xl font-semibold text-foreground mb-3">
                    Peace of Mind
                  </h3>
                  <p className="text-muted-foreground">
                    Always the right protection at the right time.
                  </p>
                </div>
              </div>)}
          </div>
        </div>
      </section>
    </>;
};